import qs from 'qs';
import moment from 'moment';
import type { FilterOptions, FilterSet, RequestState, RequestStateQuery } from '../types';

export const stateKeyToParam = {
  page: 'page',
  countryCode: 'country_code',
  region: 'region_1',
  city: 'region_2',
  arrival: 'arrival',
  durationOfStay: 'duration_of_stay',
  occupancy: 'max_occupancy',
  bedrooms: 'bedrooms',
  bathrooms: 'bathrooms',
  amenities: 'amenities',
  distance_to_beach: 'distance_to_beach',
  accommodation_type: 'accommodation_type',
  stars: 'stars',
};
export const stateKeys = Object.keys(stateKeyToParam);

const arrivalDateFormat = 'DD.MM.YYYY';
export const infinitySign = String.fromCharCode(8734);

export function stateToQuery(request: RequestState): RequestStateQuery {
  // Converts a state to a query
  return stateKeys.reduce((query, stateKey) => {
    let value;
    switch (stateKey) {
      case 'arrival': {
        const arrival = request[stateKey] as moment.Moment;
        if (!arrival || !arrival.isValid) {
          value = undefined;
          break;
        }
        value = arrival.format(arrivalDateFormat);
        break;
      }
      case 'durationOfStay': {
        const duration = request[stateKey];
        value = duration != null ? String(duration) : undefined;
        break;
      }
      case 'page':
      case 'countryCode':
      case 'region':
      case 'city':
      case 'occupancy':
        value = String(request[stateKey]);
        break;
      default:
        value = request.filterSet && (request.filterSet as any)[stateKey];
        value = Array.isArray(value) ? value.map((v) => String(v)) : String(value);
    }
    return {
      ...query,
      [(stateKeyToParam as any)[stateKey]]: value,
    };
  }, {});
}

interface RequestStateFromQuery {
  requestState: Partial<RequestState>;
  filterSet: Partial<FilterSet>;
  filterOptions: Partial<FilterOptions>;
}

export function queryToStateQuery(query: Partial<RequestStateQuery>): RequestStateFromQuery {
  const newPartialState: Partial<RequestState> = {};
  const newFilterSet: Partial<FilterSet> = {};
  const newOptions: Partial<FilterOptions> = {};

  if (query.page !== undefined) {
    newPartialState.page = parseInt(query.page, 10);
  }
  if (query.country_code !== undefined) {
    newPartialState.countryCode = query.country_code;
  }
  if (query.region_1 !== undefined) {
    newPartialState.region = query.region_1;
  }
  if (query.region_2 !== undefined) {
    newPartialState.city = query.region_2;
  }

  // arrival has valid null value
  const parsedArrival = moment(query.arrival, 'DD-MM-YYYY');
  newPartialState.arrival = parsedArrival.isValid() ? parsedArrival : null;

  // duration_of_stay has valid null value
  const duration = query.duration_of_stay ? parseInt(query.duration_of_stay, 10) : NaN;
  newPartialState.durationOfStay = !Number.isNaN(duration) ? duration : null;

  if (query.max_occupancy !== undefined) {
    newPartialState.occupancy = parseInt(query.max_occupancy, 10);
  }
  if (query.bedrooms !== undefined) {
    newFilterSet.bedrooms = parseInt(query.bedrooms, 10);
  }
  if (query.bathrooms !== undefined) {
    newFilterSet.bathrooms = parseInt(query.bathrooms, 10);
  }
  if (query.amenities !== undefined && Array.isArray(query.amenities)) {
    newFilterSet.amenities = query.amenities;
  }
  if (query.distance_to_beach !== undefined) {
    newFilterSet.distance_to_beach = parseInt(query.distance_to_beach, 10);
  }
  if (query.accommodation_type !== undefined && Array.isArray(query.accommodation_type)) {
    newFilterSet.accommodation_type = query.accommodation_type;
  }
  if (query.stars !== undefined) {
    newFilterSet.stars = parseInt(query.stars, 10);
  }
  if (query.countries !== undefined && Array.isArray(query.countries)) {
    newOptions.countries = query.countries;
  }
  if (query.regions !== undefined && Array.isArray(query.regions)) {
    newOptions.regions = query.regions;
  }
  if (query.cities !== undefined && Array.isArray(query.cities)) {
    newOptions.cities = query.cities;
  }
  return {
    requestState: newPartialState,
    filterSet: newFilterSet,
    filterOptions: newOptions,
  };
}

export const queryToState = (query: string): RequestStateFromQuery => {
  const parsed = qs.parse(query, { ignoreQueryPrefix: true });
  return queryToStateQuery(parsed);
};

export default stateToQuery;
